import { Flex, Text, useToast } from "@chakra-ui/react";
import { useRef, useState } from "react";
import {
  ManagementFunnelCard,
  ManagementFunnelStageGroup,
  ManagementPurchaseOrderCard,
} from "../../../models/Management";
import { crm } from "../../../theme";
import ProcessColumn from "./ProcessColumn";
import StatusPickerPopover from "./StatusPickerPopover";
import PurchaseOrderColumn from "./PurchaseOrderColumn";

// Funil de Gestão (Comex): arrastar um card muda o Process.Status. Se a etapa
// destino agrupa mais de um status, um popover pede pra escolher qual deles.
const ProcessBoard = ({
  stages,
  purchaseOrders,
  onMove,
  onCardClick,
}: {
  stages: ManagementFunnelStageGroup[];
  purchaseOrders?: ManagementPurchaseOrderCard[];
  onMove: (card: ManagementFunnelCard, status: string) => void;
  onCardClick: (card: ManagementFunnelCard) => void;
}) => {
  const draggedCard = useRef<ManagementFunnelCard | null>(null);
  const toast = useToast();
  const [pendingDrop, setPendingDrop] = useState<{
    card: ManagementFunnelCard;
    statuses: string[];
    position: { x: number; y: number };
  } | null>(null);

  const handleDragStart = (e: React.DragEvent, card: ManagementFunnelCard) => {
    draggedCard.current = card;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", card.id);
  };

  const handleDrop = (e: React.DragEvent, stage: ManagementFunnelStageGroup) => {
    const card = draggedCard.current;
    draggedCard.current = null;
    if (!card) return;

    const statuses = (stage.statuses ?? "")
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);

    if (statuses.length === 0) {
      toast({
        title: "Etapa sem status configurado.",
        description: "Adicione ao menos um status a essa etapa para poder mover processos para cá.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (statuses.includes(card.status ?? "")) return; // já está nessa etapa

    if (statuses.length === 1) {
      onMove(card, statuses[0]);
      return;
    }

    setPendingDrop({ card, statuses, position: { x: e.clientX, y: e.clientY } });
  };

  if (stages.length === 0) {
    return (
      <Flex w="100%" justify="center" align="center" minH="300px">
        <Text color={crm.textMuted}>
          Nenhuma etapa criada. Clique em "Gerenciar etapas" para montar o funil.
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Flex gap="16px" overflowX="auto" pb="16px" h="calc(100vh - 230px)" align="stretch">
        {purchaseOrders && <PurchaseOrderColumn purchaseOrders={purchaseOrders} />}
        {stages.map((stage) => (
          <ProcessColumn
            key={stage.stageId ?? "sem-etapa"}
            stage={stage}
            onDragStartCard={handleDragStart}
            onDropInColumn={handleDrop}
            onCardClick={onCardClick}
          />
        ))}
      </Flex>

      {pendingDrop && (
        <StatusPickerPopover
          position={pendingDrop.position}
          statuses={pendingDrop.statuses}
          onPick={(status) => {
            onMove(pendingDrop.card, status);
            setPendingDrop(null);
          }}
          onClose={() => setPendingDrop(null)}
        />
      )}
    </>
  );
};

export default ProcessBoard;
